import { useCallback, useMemo, useRef } from 'react'
import { sendEventsBatched } from '../api/storiesApi'
import type { StoryAccessType } from '../types'
import type {
  LinkTargetType,
  SlideExitReason,
  StoryEvent,
  StoryOpenSource,
} from '../analytics'

interface StoryMeta {
  storyId: number
  /** Позиция кружка в ленте на момент открытия, с 1. */
  storyPosition: number
  accessType: StoryAccessType
  hasAccess: boolean
}

interface Session extends StoryMeta {
  viewSessionId: string
  /** performance.now() в момент открытия истории — база для ts_offset_ms. */
  openedAt: number
}

const uuid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

const clampMs = (n: number): number => Math.max(0, Math.round(n))

/**
 * Трекер аналитики просмотрщика историй. Копит события в буфер и шлёт батчем
 * по закрытию просмотрщика (`flush`). ts_offset_ms считается по монотонным
 * часам (performance.now()) от момента открытия истории; view_session_id —
 * новый на каждое открытие.
 */
export const useStoryTracker = () => {
  const buffer = useRef<StoryEvent[]>([])
  const session = useRef<Session | null>(null)

  const tsOffset = (): number =>
    session.current ? clampMs(performance.now() - session.current.openedAt) : 0

  const common = () => {
    const s = session.current!
    return {
      view_session_id: s.viewSessionId,
      story_id: s.storyId,
      story_position: s.storyPosition,
      access_type: s.accessType,
      has_access: s.hasAccess,
      ts_offset_ms: tsOffset(),
    }
  }

  /** Открытие истории: новая сессия + событие story_opened (ts_offset 0). */
  const openStory = useCallback((meta: StoryMeta, source: StoryOpenSource) => {
    session.current = { ...meta, viewSessionId: uuid(), openedAt: performance.now() }
    buffer.current.push({ name: 'story_opened', ...common(), source })
  }, [])

  /** Выход со слайда. */
  const viewSlide = useCallback(
    (p: {
      slideId: number
      slideIndex: number
      dwellMs: number
      pauseCount?: number
      exitReason: SlideExitReason
    }) => {
      if (!session.current) return
      buffer.current.push({
        name: 'slide_viewed',
        ...common(),
        slide_id: p.slideId,
        slide_index: p.slideIndex,
        dwell_ms: clampMs(p.dwellMs),
        pause_count: p.pauseCount ?? 0,
        exit_reason: p.exitReason,
      })
    },
    [],
  )

  /** Нажата кнопка перехода. */
  const clickLink = useCallback(
    (p: {
      slideId: number
      slideIndex: number
      targetType: LinkTargetType
      targetId: string
    }) => {
      if (!session.current) return
      buffer.current.push({
        name: 'link_clicked',
        ...common(),
        slide_id: p.slideId,
        slide_index: p.slideIndex,
        target_type: p.targetType,
        target_id: p.targetId,
      })
    },
    [],
  )

  /** Закрытие истории (любой выход). Завершает текущую сессию. */
  const closeStory = useCallback(
    (p: { lastSlideIndex: number; isCompleted: boolean }) => {
      if (!session.current) return
      buffer.current.push({
        name: 'story_closed',
        ...common(),
        last_slide_index: p.lastSlideIndex,
        is_completed: p.isCompleted,
        total_ms: tsOffset(),
      })
      session.current = null
    },
    [],
  )

  /** Отправить накопленный батч и очистить буфер. */
  const flush = useCallback(() => {
    const events = buffer.current
    if (events.length === 0) return
    buffer.current = []
    sendEventsBatched(events)
  }, [])

  // Стабильная ссылка на объект трекера (методы и так стабильны) — чтобы колбэки
  // просмотрщика, зависящие от tracker, не пересоздавались на каждый рендер.
  return useMemo(
    () => ({ openStory, viewSlide, clickLink, closeStory, flush }),
    [openStory, viewSlide, clickLink, closeStory, flush],
  )
}
